import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DnsIcon from '@mui/icons-material/Dns';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import InfoIcon from '@mui/icons-material/Info';
import RateReviewIcon from '@mui/icons-material/RateReview';
import VideocamIcon from '@mui/icons-material/Videocam';
import SpeedIcon from '@mui/icons-material/Speed';

const steps = [
  { label: 'Server Details', icon: <DnsIcon /> },
  { label: 'Authentication', icon: <VpnKeyIcon /> },
  { label: 'Test Connection', icon: <NetworkCheckIcon /> },
  { label: 'Site Info', icon: <InfoIcon /> },
  { label: 'Review & Create', icon: <RateReviewIcon /> },
];

function ConnectionWizard({
  open,
  onClose,
  onCreate,
  onTestConnection,
  customers,
  customersLoading,
  creating,
  error,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    base_url: '',
    api_key: '',
    group_key: '',
    name: '',
    display_name: '',
    description: '',
    customer_id: '',
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: null }));
    }
    // Clear test result if connection params change
    if (['base_url', 'api_key', 'group_key'].includes(field)) {
      setTestResult(null);
    }
  };

  const validateStep = (step) => {
    const errors = {};

    switch (step) {
      case 0: // Server Details
        if (!formData.base_url.trim()) {
          errors.base_url = 'Base URL is required';
        } else if (!formData.base_url.startsWith('http')) {
          errors.base_url = 'Base URL must start with http:// or https://';
        }
        break;
      case 1: // Authentication — fields removed, pending VMS migration
        break;
      case 3: // Site Info
        if (!formData.name.trim()) {
          errors.name = 'Name is required';
        }
        break;
      default:
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep(activeStep)) return;

    // Test connection step is optional — VMS migration pending

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const result = await onTestConnection({
        base_url: formData.base_url,
        api_key: formData.api_key,
        group_key: formData.group_key,
      });
      setTestResult(result);
    } catch (err) {
      setTestResult({
        success: false,
        error: err.response?.data?.message || err.message || 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = () => {
    if (!validateStep(3)) return;

    onCreate({
      base_url: formData.base_url,
      api_key: formData.api_key,
      group_key: formData.group_key,
      name: formData.name,
      display_name: formData.display_name || null,
      description: formData.description || null,
      customer_id: formData.customer_id || null,
    });
  };

  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      base_url: '',
      api_key: '',
      group_key: '',
      name: '',
      display_name: '',
      description: '',
      customer_id: '',
    });
    setValidationErrors({});
    setTestResult(null);
    onClose();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Server Details
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Enter the VMS server URL. This should be the base URL of your VMS installation.
            </Typography>
            <TextField
              label="Base URL"
              value={formData.base_url}
              onChange={handleChange('base_url')}
              required
              error={!!validationErrors.base_url}
              helperText={validationErrors.base_url || 'e.g., https://vms.example.com:8080'}
              fullWidth
              placeholder="https://vms.example.com:8080"
              autoFocus
            />
          </Box>
        );

      case 1: // Authentication
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Enter your VMS API credentials. You can find these in the VMS settings.
            </Typography>
            {/* api_key/group_key fields removed — pending VMS migration */}
          </Box>
        );

      case 2: // Test Connection
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
              Test the connection to your VMS server to verify the credentials are correct.
            </Typography>

            <Button
              variant="contained"
              onClick={handleTest}
              disabled={testing}
              size="large"
              startIcon={testing ? <CircularProgress size={20} /> : <NetworkCheckIcon />}
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </Button>

            {testResult && (
              <Paper
                sx={{
                  p: 2,
                  mt: 2,
                  width: '100%',
                  bgcolor: testResult.success ? 'success.light' : 'error.light',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {testResult.success ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <ErrorIcon color="error" />
                  )}
                  <Typography variant="subtitle1" fontWeight="medium">
                    {testResult.success ? 'Connection Successful!' : 'Connection Failed'}
                  </Typography>
                </Box>

                {testResult.success ? (
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <VideocamIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${testResult.camera_count || 0} cameras found`}
                      />
                    </ListItem>
                    {testResult.response_time_ms && (
                      <ListItem>
                        <ListItemIcon>
                          <SpeedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Response time: ${testResult.response_time_ms}ms`}
                        />
                      </ListItem>
                    )}
                  </List>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {testResult.error}
                    </Typography>
                    {testResult.suggestion && (
                      <Typography variant="body2" color="text.secondary">
                        Suggestion: {testResult.suggestion}
                      </Typography>
                    )}
                  </>
                )}
              </Paper>
            )}
          </Box>
        );

      case 3: // Site Info
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Enter a name and optionally assign this site to a customer.
            </Typography>
            <TextField
              label="Name"
              value={formData.name}
              onChange={handleChange('name')}
              required
              error={!!validationErrors.name}
              helperText={validationErrors.name || 'Internal identifier for the site'}
              fullWidth
              autoFocus
            />
            <TextField
              label="Display Name"
              value={formData.display_name}
              onChange={handleChange('display_name')}
              helperText="Friendly name shown in the UI (optional)"
              fullWidth
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              multiline
              rows={2}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Customer</InputLabel>
              <Select
                value={formData.customer_id}
                onChange={handleChange('customer_id')}
                label="Customer"
                disabled={customersLoading}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.cust_id} value={customer.cust_id}>
                    {customer.cust_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 4: // Review
        const selectedCustomer = customers.find((c) => c.cust_id === formData.customer_id);
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review your settings before creating the site.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2 }}>
              <List dense>
                <ListItem>
                  <ListItemText primary="Name" secondary={formData.name} />
                </ListItem>
                {formData.display_name && (
                  <ListItem>
                    <ListItemText primary="Display Name" secondary={formData.display_name} />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemText primary="Base URL" secondary={formData.base_url} />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Customer"
                    secondary={selectedCustomer?.cust_name || 'None'}
                  />
                </ListItem>
                {testResult?.camera_count > 0 && (
                  <ListItem>
                    <ListItemText
                      primary="Cameras Found"
                      secondary={testResult.camera_count}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  const isNextDisabled = () => false;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Site</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          {steps.map((step) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Divider sx={{ mb: 3 }} />

        {renderStepContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={creating}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={creating}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={isNextDisabled()}
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} /> : null}
          >
            {creating ? 'Creating...' : 'Create Site'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ConnectionWizard;
